import React from 'react';
import { FaStar, FaMapMarkerAlt, FaGraduationCap, FaCheckCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import '../../styles/tutor-card.css';

const TutorCard = ({ tutor }) => {
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = React.useState(false);
  
  // Helper function to safely extract string values
  const safeString = (value, defaultValue = 'Not specified') => {
    if (!value) return defaultValue;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return defaultValue;
  };
  
  // Safely extract and format subjects
  const getSubjects = () => {
    if (!tutor?.subjects) return [];
    if (Array.isArray(tutor.subjects)) {
      return tutor.subjects
        .filter(subject => typeof subject === 'string')
        .slice(0, 4);
    }
    return [];
  };
  
  // Safely extract main subject
  const mainSubject = getSubjects()[0] || 'General Tutoring';
  const subjectCount = getSubjects().length;
  
  // Safely extract numeric values
  const safeNumber = (value, defaultValue = 0) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const num = parseFloat(value);
      return isNaN(num) ? defaultValue : num;
    }
    return defaultValue;
  };
  
  // Safely extract rating
  const rating = safeNumber(tutor?.rating, 0);
  const totalReviews = safeNumber(tutor?.totalReviews, 0);
  const totalSessions = safeNumber(tutor?.totalSessions, 0);
  const studentsHelped = safeNumber(tutor?.studentsHelped, 0);
  const hourlyRate = safeNumber(tutor?.hourlyRate, 25);
  
  // Safely extract string values
  const tutorName = safeString(tutor?.fullName, 'Tutor Name');
  const location = safeString(tutor?.location, 'Online');
  const responseRate = safeString(tutor?.responseRate, 'Fast');
  const availability = safeString(tutor?.availability, 'Available Today');
  
  // Generate a unique color based on tutor's name
  const getInitialColor = (name) => {
    const colors = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    ];
    const index = name.length % colors.length || 0;
    return colors[index];
  };

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return 'TT';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="tutor-card simple">
      <div className="tutor-card-top">
        <div 
          className="tutor-avatar"
          style={{ background: getInitialColor(tutorName) }}
        >
          <span className="avatar-initials">{getInitials(tutorName)}</span>
          {tutor?.status === 'approved' && (
            <div className="avatar-badge"><FaCheckCircle /></div>
          )}
        </div>

        <div className="tutor-summary">
          <h3 className="tutor-name">{tutorName}</h3>
          <div className="meta-row">
            <div className="rating">
              <FaStar className={`star ${rating >= 1 ? 'filled' : ''}`} />
              <span className="rating-value">{rating > 0 ? rating.toFixed(1) : 'New'}</span>
              <span className="reviews">({totalReviews})</span>
            </div>
            <div className="location"><FaMapMarkerAlt /> {location}</div>
          </div>
          <div className="subject">{mainSubject}</div>
        </div>
      </div>

      <div className="tutor-card-bottom">
        <div className="price">
          <span className="amount">${hourlyRate}</span>
          <span className="period">/hr</span>
        </div>
        <div className="actions">
          <button className="btn btn-outline" onClick={() => navigate(`/tutor/${tutor?.uid || ''}`)} disabled={!tutor?.uid}>Profile</button>
          <button className="btn btn-primary" onClick={() => navigate(`/book/${tutor?.uid || ''}`)} disabled={!tutor?.uid}>Book</button>
        </div>
      </div>
    </div>
  );
};

export default TutorCard;